import 'ag-grid-enterprise/dist/styles/ag-grid.css';
import 'ag-grid-enterprise/dist/styles/ag-theme-material.css';
import 'ag-grid-enterprise';
import 'components/layouts/Account/AccountsGrid.scss';
import 'components/layouts/Activity/ActivitySummaryBreakdownGrid.scss';

import React, { Fragment, useEffect, useState, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';

import {
  calculateRowHeight,
  getRowClassRules,
  chooseDefs,
  ViewAllBar,
  getContextMenuItems,
  processCellForClipboard,
  MAIN_HEADER_CLASS
} from 'services/TableUtils';
import {
  ALL_LABEL,
  NON_RETIREMENT_LABEL,
  RETIREMENT_LABEL
} from 'services/Filters';
import AdjustmentsDisclosure from './AdjustmentsDisclosure';

const ActivitySummaryBreakdownGrid = ({
  accountsActivitySummary,
  maxInitialRowsToShow,
  frameworkComponents,
  performanceFilter,
  columnDefs,
  columnDefsRetirementOrder,
  defaultColDef,
  autoGroupColumnDef,
  isDesktopLayout,
  has529Only,
  sweepMap
}) => {
  const [gridApi, setGridApi] = useState(null);
  const { filter } = performanceFilter;
  const [columnApi, setColumnApi] = useState(null);
  const [showRows, setShowRows] = useState(true);
  const [printMode, setPrintMode] = useState(false);
  const currentShowRows = useRef(true);
  const firstLoad = useRef(true);
  const managementSelected = useRef(filter.label);
  const accountSelected = useRef(filter.label);
  const scrollRef = useRef(null);
  const prevLayout = useRef(isDesktopLayout);
  const filterType =
    accountSelected.current === RETIREMENT_LABEL ||
    accountSelected.current === NON_RETIREMENT_LABEL ||
    accountSelected.current === ALL_LABEL
      ? 'retirement'
      : 'productType';

  window.addEventListener('beforeprint', () => {
    setPrintMode(true);
  });

  window.addEventListener('afterprint', () => {
    setPrintMode(false);
  });

  const setExpanded = () => setShowRows(!showRows);

  const onGridReady = ({ api, columnApi }) => {
    setGridApi(api);
    setColumnApi(columnApi);
  };

  const getRowHeight = ({ node, data, api }) => {
    if (node && node.detail) {
      const allDetailRowHeight =
        (data.positions.length - 1) * api.getSizesForCurrentTheme().rowHeight;
      const gridSizes = api.getSizesForCurrentTheme();
      return allDetailRowHeight + gridSizes.headerHeight - 7;
    } else if (node && node.group) {
      return (
        api.getSizesForCurrentTheme().rowHeight +
        (node.field === 'typeAndOwners' ? node.key.length / 2 : 0)
      );
    } else if (data) {
      return calculateRowHeight(data, api, isDesktopLayout, sweepMap) + 32;
    }
  };

  const getRowClass = ({ node }) => {
    const { group, field } = node;

    if (field === 'productType') {
      return MAIN_HEADER_CLASS;
    }

    return getRowClassRules(group, field);
  };

  // Updates "View All Accounts" showing or hiding
  useEffect(() => {
    const rowsToShow = gridApi
      ? showRows
        ? maxInitialRowsToShow
        : gridApi.getDisplayedRowCount()
      : maxInitialRowsToShow;
    gridApi && gridApi.paginationSetPageSize(rowsToShow);

    if (
      firstLoad &&
      !firstLoad.current &&
      currentShowRows.current !== showRows
    ) {
      const pos = showRows ? scrollRef.current.offsetTop : window.pageYOffset;
      gridApi && gridApi.refreshClientSideRowModel();
      window.scrollTo(0, pos);
      currentShowRows.current = showRows;
    }
  }, [gridApi, maxInitialRowsToShow, showRows]);

  // updates filters on table
  useEffect(() => {
    if (
      !firstLoad.current &&
      !printMode &&
      (managementSelected.current !== filter.label ||
        accountSelected.current !== filter.label)
    ) {
      managementSelected.current = filter.label;
      accountSelected.current = filter.label;
      gridApi && gridApi.onFilterChanged();
    } else if (firstLoad.current) {
      firstLoad.current = false;
    }
  }, [gridApi, columnApi, filter, printMode]);

  // redraws rows on view change
  useEffect(() => {
    if (prevLayout.current !== isDesktopLayout) {
      gridApi && gridApi.redrawRows();
      prevLayout.current = isDesktopLayout;
    }
  }, [isDesktopLayout, gridApi, columnApi, accountsActivitySummary]);

  useEffect(() => {
    isDesktopLayout && gridApi && gridApi.sizeColumnsToFit();
  }, [gridApi, isDesktopLayout, accountsActivitySummary]);

  const postSort = (rowNodes) => {
    let nextInsertPos = 0;
    rowNodes.forEach((node, i) => {
      // Move all Retirement row nodes to top of grid
      if (
        node.group &&
        node.field === 'retirementAccount' &&
        node.groupData['ag-Grid-AutoColumn'] === 'true'
      ) {
        rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
        nextInsertPos++;
      }
    });
  };

  // resize columns based on print mode
  useEffect(() => {
    if (printMode) {
      columnApi && columnApi.sizeColumnsToFit(1175);
    } else {
      columnApi && columnApi.setColumnWidth('ag-Grid-AutoColumn', 370);
      gridApi && gridApi.sizeColumnsToFit();
    }
  }, [printMode, columnApi, gridApi]);

  return (
    <Fragment>
      <AdjustmentsDisclosure />
      <div
        className="ag-theme-material activity-summary-breakdown-grid pure-u-1"
        ref={scrollRef}>
        <AgGridReact
          columnDefs={chooseDefs(
            columnDefs,
            columnDefsRetirementOrder,
            managementSelected.current,
            accountSelected.current,
            filterType,
            has529Only
          )}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          rowData={accountsActivitySummary}
          frameworkComponents={frameworkComponents}
          getRowHeight={getRowHeight}
          getRowClass={getRowClass}
          onGridReady={onGridReady}
          processCellForClipboard={processCellForClipboard}
          getContextMenuItems={getContextMenuItems}
          enableRangeSelection
          copyHeadersToClipboard
          groupSuppressBlankHeader
          suppressAggFuncInHeader
          suppressColumnVirtualisation
          groupDefaultExpanded={-1}
          headerHeight={48}
          pagination
          paginateChildRows
          suppressPaginationPanel
          animateRows
          suppressRowTransform
          domLayout="autoHeight"
          postSort={postSort}
        />
      </div>
      {gridApi && gridApi.paginationGetRowCount() > maxInitialRowsToShow && (
        <ViewAllBar expanded={showRows} setExpanded={setExpanded}>
          {showRows ? 'View All Accounts' : 'View Less Accounts'}
        </ViewAllBar>
      )}
    </Fragment>
  );
};

ActivitySummaryBreakdownGrid.defaultProps = {
  accountsActivitySummary: []
};

export default ActivitySummaryBreakdownGrid;
